import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { DatePicker, Input, Select } from "@jhool-io/fe-components";
import { useAddUsers } from "../../../user-management/hooks/user-management.mutations";
import {
    INewUserPayload,
    UserRole,
} from "../../../user-management/types/user-management.types";
import Infobox from "../../../../components/Infobox/Infobox";
import InfoIcon from "../../../../components/Icons/InfoIcon";
import { useFetchActiveProviders } from "../../hooks/providers.queries";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import { handleFormatDatePickerValue } from "../../../../utils/helpers";
import { ProviderCredential } from "../../../clients/types/clients.types";
import useToast from "../../../../hooks/useToast";

type Options = {
    label: React.ReactNode;
    value: string;
};

interface AddProviderProps {
    onFormSubmit: () => void;
}

const schema = yup.object().shape({
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    npi: yup.string(),
    started_practicing: yup.date(),
});

export default function AddProvider({ onFormSubmit }: AddProviderProps) {
    const [providerName, setProviderName] = React.useState("");
    const [practicingStarDate, setPracticingStarDate] =
        React.useState<Date | null>(null);

    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<INewUserPayload>({
        resolver: yupResolver(schema),
    });

    const { mutate } = useAddUsers();

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const { data, error, isLoading } = useFetchActiveProviders(providerName);

    const supervisorsForSelect = data?.data.map((supervisor) => ({
        label: <div className="capitalize">{supervisor.provider_name}</div>,
        value: supervisor.provider_id,
    }));

    const isSupervisorOptionsForSelect = [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
    ];

    const credentialsForSelect = Object.values(ProviderCredential).map(
        (credential) => ({
            label: credential,
            value: credential,
        })
    );

    const onSubmit = (userData: INewUserPayload) => {
        const payload = {
            users: [
                {
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    email: userData.email,
                    roles: [UserRole.PROVIDER],
                    metadata: {
                        supervisor_id: userData.supervisor_id,
                        npi: userData.npi,
                        is_supervisor: userData.is_supervisor,
                        credential: userData.credential,
                        started_practicing: userData.started_practicing
                            ? handleFormatDatePickerValue(
                                  userData.started_practicing
                              )
                            : null,
                    },
                },
            ],
        };

        mutate(payload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: ["providers"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Provider added successfully!",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add provider at this time",
                });
            },
        });
    };

    return (
        <form id="add-provider" onSubmit={handleSubmit(onSubmit)}>
            <Infobox
                icon={<InfoIcon />}
                message="Only official work email addresses are accepted"
                className="!text-primary"
            />

            <div className="fg fg-space-between two flex mt-24">
                <Input
                    {...register("first_name")}
                    label="First name"
                    placeholder="First name"
                    hasError={!!errors.first_name}
                    errorText={errors.first_name?.message}
                    isRequired
                />
                <Input
                    {...register("last_name")}
                    label="Last name"
                    placeholder="Last name"
                    hasError={!!errors.last_name}
                    errorText={errors.last_name?.message}
                    isRequired
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    {...register("email")}
                    label="Email address"
                    placeholder="Email address"
                    type="text"
                    hasError={!!errors.email}
                    errorText={errors.email?.message}
                    isRequired
                />
                <Input
                    {...register("npi")}
                    label="NPI"
                    placeholder="NPI"
                    type="text"
                    hasError={!!errors.npi}
                    errorText={errors.npi?.message}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="is_supervisor"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Is a Supervisor"
                            placeholder="Is a Supervisor"
                            options={isSupervisorOptionsForSelect}
                            onChange={(val) =>
                                field.onChange(
                                    (
                                        val as {
                                            value: boolean;
                                            label: string;
                                        }
                                    ).value
                                )
                            }
                            isLongListInDialog
                        />
                    )}
                />
                <Controller
                    name="supervisor_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Supervisor"
                            options={
                                data || !error || !isLoading
                                    ? supervisorsForSelect
                                    : []
                            }
                            isDisabled={Boolean(error)}
                            onInputChange={(value) => setProviderName(value)}
                            filterOption={() => true}
                            isSearchable
                            placeholder={
                                (!data && isLoading && "Loading...") ||
                                (error &&
                                    !isLoading &&
                                    "Error loading providers") ||
                                (data && !isLoading && "Supervisor")
                            }
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            hasError={!!errors.supervisor_id}
                            errorText={errors.supervisor_id?.message}
                            // eslint-disable-next-line react/no-unstable-nested-components
                            noOptionsMessage={() => {
                                if (isLoading) {
                                    return <Skeleton width="100%" />;
                                }
                                return "No options";
                            }}
                            isLongListInDialog
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="credential"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Credential"
                            placeholder="Credential"
                            options={credentialsForSelect}
                            onChange={(val) =>
                                field.onChange((val as Options).value)
                            }
                            isLongListInDialog
                        />
                    )}
                />
                <Controller
                    name="started_practicing"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Started Practicing on"
                            onChange={(date) => {
                                field.onChange(date);
                                setPracticingStarDate(date);
                            }}
                            selected={practicingStarDate}
                            hasError={!!errors.started_practicing}
                            errorText={
                                errors.started_practicing?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.started_practicing?.message
                            }
                        />
                    )}
                />
            </div>
        </form>
    );
}
